"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { useLocale } from "next-intl"
import dynamic from "next/dynamic"
import { useCallback, useMemo, useState } from "react"
import { useInView } from "react-intersection-observer"
import { useInViewConfig, useTransitionUpStyles } from "ui"
import { extractProperty } from "utils"

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding-bottom: 64px;
  padding-left: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 40px;
    padding-left: max(88px, calc((100vw - 1280px) / 2 + 88px));
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding-left: max(88px, calc((100vw - 1920px) / 2 + 200px));
  }
`

export interface GalleryImage {
  url: string
  altText?: string
  title?: string
}

const DynamicLighthouse = dynamic(() => import("./Lighthouse"), { ssr: false })
const DynamicImagesGrid = dynamic(() => import("./ImagesGrid"), { ssr: true })
const DynamicSwiper = dynamic(() => import("./Swiper"), { ssr: true })

export default function Gallery({ data }: { data: any }) {
  const locale = useLocale()
  const isRTL = locale === "ar" || locale === "he"
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [photoIndex, setPhotoIndex] = useState(0)
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)
  const title = extractProperty(data, "title") as string
  const galleryImages = useMemo(
    () =>
      (extractProperty(data, "galleryImages", true) as GalleryImage[]) || [],
    [data],
  )

  const lightboxSlides = useMemo(
    () =>
      galleryImages.map((img) => ({
        src: img.url,
        alt: img.altText || "",
        title: img.altText || "",
      })),
    [galleryImages],
  )

  const openLightbox = useCallback((index: number) => {
    setPhotoIndex(index)
    setLightboxOpen(true)
  }, [])

  if (galleryImages.length === 0) {
    return null
  }
  return (
    <StyledSection ref={ref} style={transitionStyles}>
      <Typography variant="h3">{title}</Typography>
      {galleryImages.length > 4 ? (
        <DynamicImagesGrid
          openLightbox={openLightbox}
          galleryImages={galleryImages}
        />
      ) : (
        <DynamicSwiper
          galleryImages={galleryImages}
          isRTL={isRTL}
          openLightbox={openLightbox}
        />
      )}

      {lightboxOpen && (
        <DynamicLighthouse
          handleClose={() => setLightboxOpen(false)}
          lightboxSlides={lightboxSlides}
          open={lightboxOpen}
          startingIndex={photoIndex}
          isRTL={isRTL}
          title={title}
        />
      )}
    </StyledSection>
  )
}
